import { Stack, Typography } from '@mui/material';
import { Checkbox, RadioButton, RadioButtonGroup, SortedSelect, TextField } from '../../ui';
import { parseItemOptions, isQuestionnaireItemRequired, QuestionnaireItem } from './QuestionnaireCRUD.utils';

export type QuestionnaireItemAnswerFieldProps = {
    item: QuestionnaireItem;
    value: string | boolean | undefined;
    onChange: (value: string | boolean) => void;
    error?: boolean;
    helperText?: string;
    fieldKey: string;
};

/**
 * Renders a supplier-facing answer control for a questionnaire item.
 * Control selection mirrors {@link QuestionnaireBuilder} item types.
 */
export function QuestionnaireItemAnswerField({
    item,
    value,
    onChange,
    error,
    helperText,
    fieldKey,
}: QuestionnaireItemAnswerFieldProps) {
    const required = isQuestionnaireItemRequired(item);

    if (item.type === 'Label') {
        return (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.name}
            </Typography>
        );
    }

    if (item.type === 'Checkbox') {
        const leftLabel = item.leftLabel.trim();

        return (
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ maxWidth: '100%' }}>
                    <Typography
                        variant="body1"
                        sx={{
                            width: '11rem',
                            flexShrink: 0,
                            overflowWrap: 'break-word',
                            visibility: leftLabel ? 'visible' : 'hidden',
                        }}
                    >
                        {leftLabel || '\u00a0'}
                    </Typography>
                    <Checkbox
                        checked={value === true || value === 'true'}
                        onChange={(e) => onChange(e.target.checked)}
                        label={item.name}
                        required={required}
                        testId={`questionnaire-${fieldKey}-checkbox`}
                        stackSx={{ width: 'auto', height: 'auto', flexShrink: 0 }}
                    />
                </Stack>
                {helperText ? (
                    <Typography variant="caption" color="error">
                        {helperText}
                    </Typography>
                ) : null}
            </Stack>
        );
    }

    if (item.type === 'RadioButton') {
        const options = parseItemOptions(item.options).map((option) => ({
            label: option.label,
            value: option.value,
        }));

        return (
            <RadioButtonGroup
                label={item.name}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(e.target.value)}
                error={error}
                helperText={helperText}
                required={required}
            >
                {options.map((option) => (
                    <RadioButton key={option.value} value={option.value} label={option.label} />
                ))}
            </RadioButtonGroup>
        );
    }

    if (item.type === 'YesNoDropdown') {
        const options = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        return (
            <SortedSelect
                label={item.name}
                value={options.find((option) => option.value === value) ?? null}
                options={options}
                onChange={(option) => onChange(option?.value ?? '')}
                error={error}
                helperText={helperText}
                required={required}
                disableSorting
            />
        );
    }

    if (item.type === 'Dropdown') {
        const options = parseItemOptions(item.options).map((option) => ({
            label: option.label,
            value: option.value,
        }));

        return (
            <SortedSelect
                label={item.name}
                value={options.find((option) => option.value === value) ?? null}
                options={options}
                onChange={(option) => onChange(option?.value ?? '')}
                error={error}
                helperText={helperText}
                required={required}
                disableSorting
            />
        );
    }

    const stringValue = typeof value === 'string' ? value : '';

    return (
        <TextField
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            label={item.name}
            multiline={item.type === 'MultiLineTextField'}
            rows={item.type === 'MultiLineTextField' ? 3 : undefined}
            error={error}
            helperText={helperText}
            testId={`questionnaire-${fieldKey}`}
            required={required}
        />
    );
}
